import { create, StoreApi } from "zustand"
import {
  SystemConfig,
  User,
  ConversationContext,
  Workspace,
  History,
  Organization,
  Profile,
} from "./types"
import { LLMModel } from "./hooks/api/llm"

interface State {
  sysConfig: SystemConfig
  userMe: User | null
  conversationContext: ConversationContext | null
  organizations: Organization[]
  workspaces: Workspace[]
  currentOrganization: Organization | null
  currentHistory: History | null
  isCopilotOpen: boolean
  copilotMode: "floating" | "drawer"
  copilotDrawerWidth: number
  copilotNewChatRequestNonce: number
  availableModels: LLMModel[]
  availableImageModels: LLMModel[]
  setSysConfig: (config: SystemConfig) => void
  setUserMe: (user: User | null) => void
  updateUserMeProfile: (profile: Profile) => void
  setConversationContext: (context: ConversationContext | null) => void
  setOrganizations: (
    organizations: Organization[] | ((prev: Organization[]) => Organization[])
  ) => void
  setWorkspaces: (
    workspaces: Workspace[] | ((prev: Workspace[]) => Workspace[])
  ) => void
  setCurrentOrganization: (organization: Organization | null) => void
  setCurrentHistory: (history: History | null) => void
  setIsCopilotOpen: (open: boolean) => void
  setCopilotMode: (mode: "floating" | "drawer") => void
  setCopilotDrawerWidth: (width: number) => void
  requestCopilotNewChat: () => void
  setAvailableModels: (models: LLMModel[]) => void
  setAvailableImageModels: (models: LLMModel[]) => void
}

const buildActions = (
  set: StoreApi<State>["setState"],
  get: StoreApi<State>["getState"]
) => {
  const updateCurrentOrganization = (organization: Organization | null) => {
    set({ currentOrganization: organization })
    if (organization) {
      const updatedOrganizations = get().organizations.map((org) =>
        org.id === organization.id ? organization : org
      )
      set({ organizations: updatedOrganizations })
    }
  }

  return {
    setSysConfig: (config: SystemConfig) => set({ sysConfig: config }),
    setUserMe: (user: User | null) => {
      set({ userMe: user })

      if (user === null) {
        updateCurrentOrganization(null)
        return
      }

      // Update organization owner if user is owner
      const currentOrg = get().currentOrganization
      if (currentOrg?.owner?.id === user?.id) {
        currentOrg.owner = user
        updateCurrentOrganization(currentOrg)
      }
    },
    updateUserMeProfile: (profile: Profile) => {
      const userMe = get().userMe
      if (!userMe) return
      set({ userMe: { ...userMe, profile } })
    },
    setConversationContext: (context: ConversationContext | null) => {
      set({ conversationContext: context })
    },
    setOrganizations: (
      organizations: Organization[] | ((prev: Organization[]) => Organization[])
    ) =>
      set((state) => ({
        organizations:
          typeof organizations === "function"
            ? organizations(state.organizations)
            : organizations,
      })),
    setWorkspaces: (
      workspaces: Workspace[] | ((prev: Workspace[]) => Workspace[])
    ) =>
      set((state) => ({
        workspaces:
          typeof workspaces === "function"
            ? workspaces(state.workspaces)
            : workspaces,
      })),
    setCurrentOrganization: updateCurrentOrganization,
    setCurrentHistory: (history: History | null) =>
      set({ currentHistory: history }),
    setIsCopilotOpen: (open: boolean) => set({ isCopilotOpen: open }),
    setCopilotMode: (mode: "floating" | "drawer") => set({ copilotMode: mode }),
    setCopilotDrawerWidth: (width: number) =>
      set({ copilotDrawerWidth: width }),
    requestCopilotNewChat: () =>
      set((state) => ({
        copilotNewChatRequestNonce: state.copilotNewChatRequestNonce + 1,
      })),
    setAvailableModels: (models: LLMModel[]) =>
      set({ availableModels: models }),
    setAvailableImageModels: (models: LLMModel[]) =>
      set({ availableImageModels: models }),
  }
}

const defaultSysConfig: SystemConfig = {
  serverRoot: "http://localhost:8000",
  clientChannelDomain: "beta-web.codeer.ai",
  threadHistoryPageEnabled: false,
}

const initStates = {
  sysConfig: defaultSysConfig,
  userMe: null,
  profile: null,
  conversationContext: null,
  organizations: [],
  workspaces: [],
  currentOrganization: null,
  currentHistory: null,
  isCopilotOpen: false,
  copilotMode: "floating" as const,
  copilotDrawerWidth: 420,
  copilotNewChatRequestNonce: 0,
  availableModels: [],
  availableImageModels: [],
}

export const useStore = create<State>((set, get) => ({
  // states
  ...initStates,

  // actions
  ...buildActions(set, get),
}))
